import TrafficLightComponent from "@/Components/TrafficLightComponent";
import { WizardStep } from "@/Components/wizard/wizardStep";

export const getBasicTourSteps: WizardStep[] = [
        {
            stepId: 1,
            stepName: "Page Lock",
            stepDescription: "This shows if the page is locked for editing. If the page is locked by another user you will be in view only mode.",
            isCompleted: false,
            stepComponentId: "pageLockSwitch",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 2,
            stepName: "Status Report Overview",
            stepDescription: "This section provides a summary of the project's current status, including progress chart, key metrics and overall health.",
            isCompleted: false,
            stepComponentId: "status-report-chart",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 3,
            stepName: "Estimated Close Date",
            stepDescription: "This shows the estimated close date for the project.",
            isCompleted: false,
            stepComponentId: "estimated-close-date",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 4,
            stepName: "Scope Health",
            stepDescription: getScopeHealthBasicInfo(),
            isCompleted: false,
            moreInfo: getScopeHealthMoreInfo(),
            stepComponentId: "scope-traffic-light",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 5,
            stepName: "Schedule Health",
            stepDescription: getScheduleHealthBasicInfo(),
            isCompleted: false,
            moreInfo: getScheduleHealthMoreInfo(),
            stepComponentId: "schedule-traffic-light",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 6,
            stepName: "Resource Health",
            stepDescription: getResourceHealthBasicInfo(),
            isCompleted: false,
            moreInfo: getResourceHealthMoreInfo(),
            stepComponentId: "resources-traffic-light",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 7,
            stepName: "Financial Health",
            stepDescription: getFinancialHealthBasicInfo(),
            isCompleted: false,
            moreInfo: getFinancialHealthMoreInfo(),
            stepComponentId: "financial-traffic-light",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 8,
            stepName: "Other Health",
            stepDescription: getOtherHealthBasicInfo(),
            isCompleted: false,
            moreInfo: getOtherHealthMoreInfo(),
            stepComponentId: "other-traffic-light",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 9,
            stepName: "Progress Chart",
            stepDescription: "This chart visualizes the project's progress over time, highlighting key milestones and deliverables.",
            isCompleted: false,
            stepComponentId: "progress-chart",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 10,
            stepName: "Deliverables Only Filter",
            stepDescription: "Use this filter to view only deliverables in the progress chart.",
            isCompleted: false,
            stepComponentId: "deliverables-only-button",
            sendClick: true,
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 11,
            stepName: "Time Frame Selector",
            stepDescription: "Select the time frame for the progress chart to view project progress over different periods.",
            isCompleted: false,
            stepComponentId: "select-time-frame-button",
            sendClick: true,
            showAboveDialog: true,
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 12,
            stepName: "Scrolling the Chart",
            stepDescription: "Use this slider to scroll the progress chart left or right to view different time periods.",
            isCompleted: false,
            stepComponentId: "display-range-slider",
            sendClick: true,
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 13,
            stepName: "Key Events Section",
            stepDescription: "This section highlights significant events and milestones in the project's timeline.",
            isCompleted: false,
            stepComponentId: "key-events-table",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 14,
            stepName: "Key Event",
            stepDescription: "Clicking on a row in the key events table will take you to the Plan page where you can see more details about that event.",
            isCompleted: false,
            stepComponentId: "key-events-table-row",
            useClassSelector: true,
            classSelectorNumber: 0,
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 15,
            stepName: "Risks, Issues, and Support Requests Section",
            stepDescription: "Risks, Issues, and Support Requests can be chosen to be included in the Status Report. This section provides an overview of these items.",
            isCompleted: false,
            stepComponentId: "risks-issues-support-requests",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 16,
            stepName: "Risks, Issues, and Support Requests",
            stepDescription: "Clicking on a row in the Risks, Issues, and Support Requests table will take you to the respective page where you can see more details about that item.",
            isCompleted: false,
            stepComponentId: "risks-issues-table-row",
            useClassSelector: true,
            classSelectorNumber: 0,
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 17,
            stepName: "Activities Next-Steps Section",
            stepDescription: "This section allows you to add an overview of activities and next-steps for the project.",
            isCompleted: false,
            stepComponentId: "activities-next-steps",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 18,
            stepName: "Create New Status Report",
            stepDescription: "Click this button to create a new status report for the project.",
            isCompleted: false,
            stepComponentId: "create-report-button",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 19,
            stepName: "Update Status Report",
            stepDescription: "Click this button to update the current status report with any changes made.",
            isCompleted: false,
            stepComponentId: "update-report-button",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        }
    ];

function getScopeHealthBasicInfo(): JSX.Element {
    return (
        <div className="w-[300px]">
            This traffic light indicates the health of the project scope based on current change requests. 
            If the light is not green, you can click on it to be taken to the Project Change Requests page to see more details.
        </div>
    );
}

function getScopeHealthMoreInfo(): JSX.Element {
    return (
        <div className="flex flex-col w-[300px] p-2">
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={4} Size={5} Animate={false} /></div><div className="">If no open change requests the scope health is green</div></div>
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={2} Size={5} Animate={false} /></div><div className="my-4">If there are open change requests the scope health will be amber</div></div>
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={1} Size={5} Animate={false} /></div><div className="">If the change requests are high impact the scope health will be red</div></div>
        </div>
    );
}

function getScheduleHealthBasicInfo(): JSX.Element {
    return (
        <div className="w-[300px]">
            This traffic light indicates the health of the project schedule based on current tasks that are overdue.
            If the light is not green, you can click on it to be taken to the Project Plan page to see more details.
        </div>
    );
}

function getScheduleHealthMoreInfo(): JSX.Element {
    return (
        <div className="flex flex-col w-[300px] p-2">
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={4} Size={5} Animate={false} /></div><div className="">No overdue tasks means the schedule health is green</div></div>
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={2} Size={5} Animate={false} /></div><div className="my-4">If there are overdue tasks the schedule health will be amber</div></div>
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={1} Size={5} Animate={false} /></div><div className="">If there are overdue tasks which have dependents or have exceeded the work package end date, the schedule health will be red</div></div>
        </div>
    );
}

function getResourceHealthBasicInfo(): JSX.Element {
    return (
        <div className="w-[300px]">
            This traffic light indicates the health of the project resources based on current resource requests.
            If the light is not green, you can click on it to be taken to the Support Requests page to see more details.
        </div>
    );
}

function getResourceHealthMoreInfo(): JSX.Element {
    return (
        <div className="flex flex-col w-[300px] p-2">
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={4} Size={5} Animate={false} /></div><div className="">No Resource Support Requests means the resource health is green</div></div>
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={2} Size={5} Animate={false} /></div><div className="my-4">If there are Resource Support Requests of medium impact the resource health will be amber</div></div>
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={1} Size={5} Animate={false} /></div><div className="">If there are Resource Support Requests of high impact the resource health will be red</div></div>
        </div>
    );
}

function getFinancialHealthBasicInfo(): JSX.Element {
    return (
        <div className="w-[300px]">
            This traffic light indicates the health of the project finances based on current budget status.
            If the light is not green, you can click on it to be taken to the Support Requests page to see more details.
        </div>
    );
}

function getFinancialHealthMoreInfo(): JSX.Element {
    return (
        <div className="flex flex-col w-[300px] p-2">
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={4} Size={5} Animate={false} /></div><div className="">No Financial Support Requests means the financial health is green</div></div>
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={2} Size={5} Animate={false} /></div><div className="my-4">If there are Financial Support Requests of medium impact the financial health will be amber</div></div>
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={1} Size={5} Animate={false} /></div><div className="">If there are Financial Support Requests of high impact the financial health will be red</div></div>
        </div>
    );
}

function getOtherHealthBasicInfo(): JSX.Element {
    return (
        <div className="w-[300px]">
            This traffic light indicates the health of the project based on other factors not covered by scope, schedule, resource, or financial health.
            If the light is not green, you can click on it to be taken to the Support Requests page to see more details.
        </div>
    );
}

function getOtherHealthMoreInfo(): JSX.Element {
    return (
        <div className="flex flex-col w-[300px] p-2">
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={4} Size={5} Animate={false} /></div><div className="">No Other Support Requests means the other health is green</div></div>
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={2} Size={5} Animate={false} /></div><div className="my-4">If there are Other Support Requests of medium impact the other health will be amber</div></div>
                <div className="flex flex-row"><div className="my-auto pr-4"><TrafficLightComponent TrafficLightColour={1} Size={5} Animate={false} /></div><div className="">If there are Other Support Requests of high impact the other health will be red</div></div>
        </div>
    );
}